import { Field, Form as FormikForm, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Form, Stack } from 'react-bootstrap';
import * as Yup from 'yup';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText, TimesheetIntro } from '../../../../components/generic';
import CurrencyCell from '../../../../components/tables/cellComponents/CurrencyCell';
import SFCTable from '../../../../components/tables/SFCTable';
import { useToast } from '../../../../context/ToastContext';
import {
  useCreateTimesheetOverrideMutation,
  useGetCurrentEntriesQuery
} from '../../../../services/endpoints/finance/timesheets';
import { useGetInstructorsQuery, User } from '../../../../services/endpoints/people/user';
import { formatUserName, getCurrentUser } from '../../../../services/helper';
import { timesheet_entries } from '../../../../types/finance.types';
import { isLocal } from '../../../../utils/consts';
import { getCurrentTimesheetColumns } from '../../../../utils/consts/columnDefs/timesheetColumns';
import { formatDateString, getErrorMessage } from '../../../../utils/utils';

export const CurrentTimesheetView = () => {
  const { addToast } = useToast();
  const currentUser = getCurrentUser();
  const isLocalUser = isLocal(currentUser?.user_type_id!);
  const [userId, setUserId] = useState<number>(currentUser?.id ?? 0);
  const { data, error, isLoading } = useGetCurrentEntriesQuery(userId, { skip: !userId });
  const {
    data: instructors,
    error: instructorsError,
    isLoading: instructorsLoading
  } = useGetInstructorsQuery(
      { scope: 'active_instructors', data: { entries: 1000, orderBy: { field: 'first_name', order: 'asc' } } },
    { skip: !isLocalUser }
  );
  const [createTimesheetOverride] = useCreateTimesheetOverrideMutation();

  const timesheetEntries =
    data?.override_submission_open === false ? [] : (data?.payroll?.timesheetEntries ?? []);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  const validationSchema = Yup.object({
    instructorId: Yup.number()
  });

  const onSubmit = (arg: any) => {
    setUserId(parseInt(arg.instructor));
  };

  const tableValidationSchema = Yup.object({
    timesheet_override_type: Yup.number()
      .transform((_value, originalValue) => {
        const parsed = Number(originalValue);
        return Number.isNaN(parsed) ? undefined : parsed;
      })
      .min(1, 'Please select an additional reimbursement type')
      .required('Please select an additional reimbursement type'),
    override_request: Yup.string(),
    lesson_id: Yup.number(),
    instructor_id: Yup.number(),
    instructor_current_rate: Yup.number(),
    instructor_current_reimbursement: Yup.number()
  });

  const onTableFormSubmit = (values: any, _row: timesheet_entries) => {
    createTimesheetOverride(values)
      .unwrap()
      .then(() => {
        addToast('Successfully created timesheet override', 'success');
      })
      .catch((error: unknown) => {
        addToast(`Failed to create timesheet override: ${getErrorMessage(error)}`, 'error');
      });
  };

  const sidebars = [<TimesheetIntro key="intro" />];

  if (data?.override_submission_open === false) {
    sidebars.push(
      <DetailSection
        key="closed"
        title="Pay period closed"
      >
        <p>
          {data.override_submission_message ??
            'Current timesheet entries are hidden after 7:00 PM on the last day of each pay period in your location\u2019s time zone.'}
        </p>
      </DetailSection>
    );
  }

  if (isLocalUser) {
    sidebars.unshift(
      <DetailSection title="Instructors">
        <Formik
          initialValues={{}}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {() => (
            <FormikForm className="text-center">
              <Form.Group controlId="instructor">
                <Stack gap={2}>
                  <Field
                    name="instructor"
                    as={Form.Select}
                    className="w-100"
                  >
                    <option>Select Instructor</option>
                    {instructors?.results?.map((ins: User) => (
                      <option
                        key={ins.id}
                        value={ins.id}
                      >
                        {formatUserName(ins)}
                      </option>
                    ))}
                  </Field>
                  <SimpleButton
                    height={45}
                    type="submit"
                    className="w-100"
                    truncate={false}
                    variant="primary"
                  >
                    Submit
                  </SimpleButton>
                </Stack>
              </Form.Group>
            </FormikForm>
          )}
        </Formik>
      </DetailSection>
    );
  }
  if (data?.payroll && data.override_submission_open !== false) {
    sidebars.push(
      <DetailSection title="Summary">
        <LabelText
          label="Starts On:"
          text={formatDateString(String(data?.start))}
        />
        <LabelText
          label="Ends On:"
          text={formatDateString(String(data?.end))}
        />
        <LabelText
          label="# of classes:"
          text={String(data?.payroll.totalLessonCount)}
        />
        <LabelText label="Total:">
          <CurrencyCell value={String(data?.payroll.payAmount)} />
        </LabelText>
        <br />
        <LabelText label="YTD Gross Pay:">
          <CurrencyCell value={String(data?.ytd.payAmount)} />
        </LabelText>
      </DetailSection>
    );
  }

  return (
    <div className="py-2">
      <SFCTable
        name={''}
        columns={getCurrentTimesheetColumns(isLocalUser)}
        data={timesheetEntries}
        count={timesheetEntries?.length}
        defaultScope={'all'}
        isLoading={isLoading}
        sidebars={sidebars}
        indexDownloadPath={currentUser?.isSuper ? `current-entries` : undefined}
        rowFormProps={
          data?.override_submission_open === false
            ? undefined
            : {
                validationSchema: tableValidationSchema,
                onSubmit: onTableFormSubmit,
                getInitialValues: (row) => ({
                  instructor_id: row.original.instructor_id,
                  lesson_id: row.original.lesson_id,
                  instructor_current_rate: row.original.instructor_session_rate,
                  instructor_current_reimbursement: row.original.instructor_session_reimbursement,
                  timesheet_override_type: 0
                })
              }
        }
      />
    </div>
  );
};
